<template>
  <v-container fluid>
    <v-card v-if="isAdmin" class="mb-4 pa-4 d-flex align-center flex-wrap">
      <div>
        <div class="text-subtitle-1 font-weight-medium">Role-Based Access Control</div>
        <div class="text-caption grey--text">
          {{
            rbacEnabled
              ? "Aktif — menu & tombol dibatasi sesuai permission role."
              : "Nonaktif — semua menu & tombol terlihat (seperti sebelum RBAC)."
          }}
        </div>
      </div>
      <v-spacer />
      <v-switch
        v-model="rbacToggle"
        :label="rbacToggle ? 'ON' : 'OFF'"
        color="primary"
        hide-details
        class="mt-0"
        @change="onToggleRbac"
      />
    </v-card>
    <v-card>
      <v-tabs v-model="tab">
        <v-tab key="roles">Role Permissions</v-tab>
        <v-tab key="users">User Roles</v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <!-- TAB 1: Role Permissions -->
        <v-tab-item key="roles">
          <v-row class="pa-4" no-gutters>
            <!-- LEFT: Role List -->
            <v-col cols="3" class="pr-3">
              <v-card
                outlined
                class="pa-2"
                style="max-height: calc(100vh - 220px); overflow-y: auto;"
              >
                <v-card-title class="d-flex align-center pa-2 pt-0">
                  <v-icon small class="mr-1">mdi-shield-account</v-icon>
                  Roles
                  <v-spacer />
                  <v-btn icon small @click="openCreateDialog">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </v-card-title>
                <v-divider class="mb-2" />
                <v-text-field
                  v-model="roleSearch"
                  dense
                  outlined
                  placeholder="Search..."
                  prepend-inner-icon="mdi-magnify"
                  hide-details
                  class="mb-2"
                />
                <v-list dense nav>
                  <v-list-item
                    v-for="role in filteredRoles"
                    :key="role.id"
                    @click="selectRole(role)"
                    :class="{ 'grey lighten-3': selectedRole?.id === role.id }"
                  >
                    <v-list-item-action v-if="role.isSystem" class="mr-0">
                      <v-icon small>mdi-lock</v-icon>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title v-text="role.name" />
                      <v-list-item-subtitle v-text="'Users: ' + role.userCount" />
                    </v-list-item-content>
                    <v-list-item-action class="d-flex flex-row align-center" style="gap: 2px;">
                      <v-btn v-if="!role.isSystem" icon x-small @click.stop="openEditDialog(role)">
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn
                        v-if="!role.isSystem"
                        icon
                        x-small
                        @click.stop="openDeleteDialog(role)"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-col>

            <!-- DIVIDER -->
            <v-col cols="1" class="d-flex justify-center pa-0">
              <v-divider vertical />
            </v-col>

            <!-- RIGHT: Permission Matrix -->
            <v-col cols="8" class="pl-3">
              <v-card
                v-if="selectedRole"
                outlined
                style="max-height: calc(100vh - 220px); overflow-y: auto;"
              >
                <v-card-title class="d-flex align-center py-2">
                  <v-icon small class="mr-1">mdi-shield-key</v-icon>
                  {{ selectedRole.name }}
                  <v-spacer />
                  <v-chip v-if="selectedRole.isSystem" x-small color="grey">system</v-chip>
                </v-card-title>
                <v-divider />
                <v-card-text class="pt-3">
                  <v-expansion-panels multiple flat>
                    <v-expansion-panel
                      v-for="group in permissionGroups"
                      :key="group.name"
                      class="mb-1"
                    >
                      <v-expansion-panel-header class="py-1" disable-icon-rotate>
                        <template v-slot:default="{ open }">
                          <div class="d-flex align-center" style="width: 100%">
                            <v-icon small :color="open ? 'primary' : ''" class="mr-1">
                              {{ open ? "mdi-chevron-down" : "mdi-chevron-right" }}
                            </v-icon>
                            <span class="text-subtitle-2 font-weight-bold">{{ group.name }}</span>
                            <v-spacer />
                            <v-chip
                              x-small
                              class="ml-2"
                              :color="countGroupSelected(group) > 0 ? 'primary' : ''"
                              outlined
                            >
                              {{ countGroupSelected(group) }}/{{ countGroupTotal(group) }}
                            </v-chip>
                          </div>
                        </template>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <!-- Menu items within module -->
                        <v-expansion-panels multiple flat class="ml-4">
                          <v-expansion-panel
                            v-for="item in group.items"
                            :key="item.prefix"
                            class="mb-1"
                          >
                            <v-expansion-panel-header
                              class="py-0 pl-2"
                              disable-icon-rotate
                              style="min-height: 36px"
                            >
                              <template v-slot:default="{ open }">
                                <div class="d-flex align-center" style="width: 100%">
                                  <v-icon x-small :color="open ? 'primary' : ''" class="mr-1">
                                    {{ open ? "mdi-chevron-down" : "mdi-chevron-right" }}
                                  </v-icon>
                                  <span class="body-2">{{ item.displayName || item.prefix }}</span>
                                  <v-spacer />
                                  <v-chip
                                    x-small
                                    class="ml-1"
                                    :color="countItemSelected(item) > 0 ? 'primary' : ''"
                                    outlined
                                  >
                                    {{ countItemSelected(item) }}/{{ countItemTotal(item) }}
                                  </v-chip>
                                </div>
                              </template>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                              <div
                                class="text-caption font-weight-bold grey--text text--darken-1 mb-1 mt-1"
                              >
                                VISIBILITY
                              </div>
                              <v-row dense>
                                <v-col
                                  v-for="perm in item.pagePermissions"
                                  :key="perm.code"
                                  cols="4"
                                  sm="3"
                                >
                                  <v-checkbox
                                    :input-value="selectedPermissionCodes.includes(perm.code)"
                                    :label="perm.name"
                                    dense
                                    hide-details
                                    class="mt-0"
                                    @change="togglePermission(perm.code)"
                                  />
                                </v-col>
                              </v-row>
                              <div
                                class="text-caption font-weight-bold grey--text text--darken-1 mb-1 mt-2"
                              >
                                ACTIONS
                              </div>
                              <v-row v-if="item.actionPermissions.length > 0" dense>
                                <v-col
                                  v-for="perm in item.actionPermissions"
                                  :key="perm.code"
                                  cols="4"
                                  sm="3"
                                >
                                  <v-checkbox
                                    :input-value="selectedPermissionCodes.includes(perm.code)"
                                    :label="perm.name"
                                    dense
                                    hide-details
                                    class="mt-0"
                                    @change="togglePermission(perm.code)"
                                  />
                                </v-col>
                              </v-row>
                              <div v-else class="text-caption grey--text">No actions</div>
                            </v-expansion-panel-content>
                          </v-expansion-panel>
                        </v-expansion-panels>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                  <v-btn
                    color="primary"
                    @click="savePermissions"
                    :loading="saving"
                    :disabled="!hasChanges"
                    class="mt-4"
                  >
                    Save Changes
                  </v-btn>
                </v-card-text>
              </v-card>
              <v-card
                v-else
                outlined
                class="d-flex align-center justify-center"
                style="height: 300px"
              >
                <div class="text-center grey--text">
                  <v-icon x-large color="grey lighten-1">mdi-shield-account</v-icon>
                  <p class="mt-4">Select a role to configure permissions</p>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-tab-item>

        <!-- TAB 2: User Roles -->
        <v-tab-item key="users">
          <v-row class="pa-4" no-gutters>
            <!-- LEFT: User List -->
            <v-col cols="4" class="pr-3">
              <v-card
                outlined
                class="pa-2"
                style="max-height: calc(100vh - 220px); overflow-y: auto;"
              >
                <v-card-title class="d-flex align-center pa-2 pt-0">
                  <v-icon small class="mr-1">mdi-account-group</v-icon>
                  Users
                </v-card-title>
                <v-divider class="mb-2" />
                <v-text-field
                  v-model="userSearch"
                  dense
                  outlined
                  placeholder="Search name or email..."
                  prepend-inner-icon="mdi-magnify"
                  hide-details
                  class="mb-2"
                />
                <v-list dense nav>
                  <v-list-item
                    v-for="user in filteredUsers"
                    :key="user.id"
                    @click="selectUser(user)"
                    :class="{ 'grey lighten-3': selectedUser?.id === user.id }"
                  >
                    <v-list-item-content>
                      <v-list-item-title
                        >{{ user.firstName }} {{ user.lastName }}</v-list-item-title
                      >
                      <v-list-item-subtitle>{{ user.email }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-col>

            <!-- DIVIDER -->
            <v-col cols="1" class="d-flex justify-center pa-0">
              <v-divider vertical />
            </v-col>

            <!-- RIGHT: User Detail & Role Assignment -->
            <v-col cols="7" class="pl-3">
              <v-card v-if="selectedUser" outlined style="max-height: calc(100vh - 220px);">
                <v-card-title class="d-flex align-center py-2">
                  <v-icon small class="mr-1">mdi-account-details</v-icon>
                  {{ selectedUser.firstName }} {{ selectedUser.lastName }}
                </v-card-title>
                <v-card-subtitle class="pb-0">{{ selectedUser.email }}</v-card-subtitle>
                <v-divider class="mt-2" />
                <v-card-text class="pt-3 overflow-y-auto">
                  <div class="text-subtitle-2 font-weight-bold mb-2">
                    <v-icon x-small class="mr-1">mdi-shield-check</v-icon>
                    Role Assignment
                  </div>
                  <v-radio-group v-model="userSelectedRole" column>
                    <v-radio
                      v-for="role in roles"
                      :key="role.id"
                      :label="role.name"
                      :value="role.name"
                      dense
                    />
                  </v-radio-group>
                  <v-btn
                    color="primary"
                    class="mt-4"
                    @click="saveUserRolesAction"
                    :disabled="!userRolesChanged"
                  >
                    <v-icon small class="mr-1">mdi-content-save</v-icon>
                    Save Role
                  </v-btn>
                </v-card-text>
              </v-card>
              <v-card
                v-else
                outlined
                class="d-flex align-center justify-center"
                style="height: 300px"
              >
                <div class="text-center grey--text">
                  <v-icon x-large color="grey lighten-1">mdi-account-group</v-icon>
                  <p class="mt-4">Select a user to assign roles</p>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <!-- Create/Edit Role Dialog -->
    <v-dialog v-model="dialog" max-width="400">
      <v-card>
        <v-card-title>
          <v-icon small class="mr-1">mdi-shield-edit</v-icon>
          {{ editingRole ? "Edit Role" : "New Role" }}
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="formName"
            label="Name"
            dense
            outlined
            class="mb-2"
            :disabled="editingRole?.isSystem"
          />
          <v-text-field v-model="formDesc" label="Description" dense outlined />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="saveRole">{{ editingRole ? "Update" : "Create" }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card>
        <v-card-title>
          <v-icon small class="mr-1" color="error">mdi-delete-alert</v-icon>
          Delete Role
        </v-card-title>
        <v-card-text>
          <div v-if="deleteBlocked" class="error--text">
            <v-icon color="error" class="mr-1">mdi-alert-circle</v-icon>
            Role "{{ selectedRole?.name }}" is used by {{ deleteUserList.length }} user(s):
            <ul class="mt-2">
              <li v-for="name in deleteUserList" :key="name">{{ name }}</li>
            </ul>
            <p class="mt-2">Move users to another role before deleting.</p>
          </div>
          <div v-else>Delete "{{ selectedRole?.name }}"? This cannot be undone.</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn v-if="!deleteBlocked" color="error" @click="confirmDelete">Delete</v-btn>
          <v-btn v-else color="primary" @click="deleteDialog = false">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";

export default {
  name: "Rbac",
  data() {
    return {
      tab: 0,
      rbacToggle: false,
      roleSearch: "",
      userSearch: "",
      selectedRole: null,
      selectedPermissionCodes: [],
      originalPermissionCodes: [],
      saving: false,
      dialog: false,
      deleteDialog: false,
      deleteBlocked: false,
      deleteUserList: [],
      editingRole: null,
      formName: "",
      formDesc: "",
      selectedUser: null,
      userSelectedRole: null,
      originalUserRole: null,
    };
  },
  computed: {
    ...mapState("rbac", ["roles", "permissions", "users"]),
    ...mapGetters("rbac", ["permissionGroups"]),
    isAdmin() {
      const ud = this.$store.state.auth.userDetail;
      return !!ud && ud.role === "ROLE_ADMIN";
    },
    rbacEnabled() {
      return this.$store.state.rbac.rbacEnabled;
    },
    filteredRoles() {
      if (!this.roleSearch) return this.roles;
      return this.roles.filter(r => r.name.toLowerCase().includes(this.roleSearch.toLowerCase()));
    },
    filteredUsers() {
      const users = this.users;
      if (!this.userSearch) return users;
      const q = this.userSearch.toLowerCase();
      return users.filter(
        u =>
          (u.firstName + " " + u.lastName).toLowerCase().includes(q) ||
          (u.email || "").toLowerCase().includes(q)
      );
    },
    hasChanges() {
      if (this.selectedPermissionCodes.length !== this.originalPermissionCodes.length) return true;
      return this.selectedPermissionCodes.some(c => !this.originalPermissionCodes.includes(c));
    },
    userRolesChanged() {
      return this.userSelectedRole !== this.originalUserRole;
    },
  },
  async mounted() {
    await this.$store.dispatch("rbac/fetchRoles");
    await this.$store.dispatch("rbac/fetchPermissions");
    await this.$store.dispatch("rbac/fetchUsers");
    await this.$store.dispatch("rbac/fetchRbacEnabled");
    this.rbacToggle = this.$store.state.rbac.rbacEnabled;
  },
  methods: {
    ...mapActions("rbac", ["saveRolePermissions", "saveUserRole"]),
    async onToggleRbac(value) {
      try {
        await this.$store.dispatch("rbac/setRbacEnabled", value);
        this.$store.dispatch(
          "toast",
          { type: "success", message: value ? "RBAC diaktifkan" : "RBAC dinonaktifkan" },
          { root: true }
        );
      } catch (e) {
        this.rbacToggle = this.$store.state.rbac.rbacEnabled;
        this.$store.dispatch(
          "toast",
          { type: "error", message: "Gagal mengubah status RBAC" },
          { root: true }
        );
      }
    },
    countGroupSelected(group) {
      let total = 0;
      for (const item of group.items) {
        total += this.countItemSelected(item);
      }
      return total;
    },
    countGroupTotal(group) {
      let total = 0;
      for (const item of group.items) {
        total += this.countItemTotal(item);
      }
      return total;
    },
    countItemSelected(item) {
      const codes = [...item.pagePermissions, ...item.actionPermissions].map(p => p.code);
      return codes.filter(c => this.selectedPermissionCodes.includes(c)).length;
    },
    countItemTotal(item) {
      return item.pagePermissions.length + item.actionPermissions.length;
    },
    async selectRole(role) {
      this.selectedRole = role;
      const codes = await this.$store.dispatch("rbac/fetchRolePermissions", role.id);
      this.selectedPermissionCodes = [...(codes || [])];
      this.originalPermissionCodes = [...(codes || [])];
    },
    togglePermission(code) {
      const idx = this.selectedPermissionCodes.indexOf(code);
      if (idx >= 0) {
        this.selectedPermissionCodes.splice(idx, 1);
      } else {
        this.selectedPermissionCodes.push(code);
      }
    },
    async savePermissions() {
      this.saving = true;
      try {
        await this.saveRolePermissions({
          roleId: this.selectedRole.id,
          permissionCodes: this.selectedPermissionCodes,
        });
        this.originalPermissionCodes = [...this.selectedPermissionCodes];
        this.$store.dispatch(
          "toast",
          { type: "success", message: "Permissions saved" },
          { root: true }
        );
      } finally {
        this.saving = false;
      }
    },
    openCreateDialog() {
      this.editingRole = null;
      this.formName = "";
      this.formDesc = "";
      this.dialog = true;
    },
    openEditDialog(role) {
      this.editingRole = role;
      this.formName = role.name;
      this.formDesc = role.description || "";
      this.dialog = true;
    },
    async saveRole() {
      try {
        if (this.editingRole) {
          await this.$store.dispatch("rbac/updateRole", {
            id: this.editingRole.id,
            body: { name: this.formName, description: this.formDesc },
          });
        } else {
          const created = await this.$store.dispatch("rbac/createRole", {
            name: this.formName,
            description: this.formDesc,
          });
          if (created) this.selectRole(created);
        }
        this.dialog = false;
        this.$store.dispatch("toast", { type: "success", message: "Role saved" }, { root: true });
      } catch (e) {
        this.$store.dispatch(
          "toast",
          { type: "error", message: "Failed to save role" },
          { root: true }
        );
      }
    },
    async openDeleteDialog(role) {
      this.selectedRole = role;
      this.deleteDialog = true;
      this.deleteBlocked = false;
      this.deleteUserList = [];
      try {
        const users = await this.$store.dispatch("rbac/fetchRoleUsers", role.id);
        this.deleteUserList = users;
        this.deleteBlocked = this.deleteUserList.length > 0;
      } catch (e) {
        this.deleteBlocked = false;
      }
    },
    async confirmDelete() {
      try {
        await this.$store.dispatch("rbac/deleteRole", this.selectedRole.id);
        this.deleteDialog = false;
        this.selectedRole = null;
        this.$store.dispatch("toast", { type: "success", message: "Role deleted" }, { root: true });
      } catch (e) {
        this.$store.dispatch(
          "toast",
          { type: "error", message: "Failed to delete role" },
          { root: true }
        );
      }
    },
    async selectUser(user) {
      this.selectedUser = user;
      try {
        const role = await this.$store.dispatch("rbac/fetchUserRole", user.id);
        this.userSelectedRole = role || null;
        this.originalUserRole = role || null;
      } catch (e) {
        this.userSelectedRole = null;
        this.originalUserRole = null;
      }
    },
    setUserRole(roleName) {
      this.userSelectedRole = roleName;
    },
    async saveUserRolesAction() {
      try {
        await this.saveUserRole({
          userId: this.selectedUser.id,
          roleName: this.userSelectedRole,
        });
        this.originalUserRole = this.userSelectedRole;
        await this.$store.dispatch("rbac/fetchUsers");
        this.$store.dispatch(
          "toast",
          { type: "success", message: "User role saved" },
          { root: true }
        );
      } catch (e) {
        this.$store.dispatch(
          "toast",
          { type: "error", message: "Failed to save user role" },
          { root: true }
        );
      }
    },
  },
};
</script>
